import { useAtom, useAtomValue } from "jotai";
import styles from "../styles.module.scss";
import { ReactComponent as TrashIcon } from "../../../assets/trash.svg";
import { ReactComponent as EditIcon } from "../../../assets/pencil-paper.svg";
import { pageIndexAtom, userAtom } from "../../../utils/store";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Spin } from "antd";
import {
  createUpdateWorkHistory,
  deleteWorkHistory,
  getWorkHistoryByApplicantId,
} from "../../../utils/request";
import { format } from "date-fns";
import WorkHistoryForm from "./form";
import { formatMonthYear } from "../../../utils/formatDate";
import OnboardingButtonGroup from "../onboardingButtonGroup";

const WorkHistory = () => {
  const user = useAtomValue(userAtom);
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);

  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [activeWorkHistoryId, setActiveWorkHistoryId] = useState(0);

  const queryClient = useQueryClient();

  const { notification } = App.useApp();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["get-work-history"],
    queryFn: () => getWorkHistoryByApplicantId(user?.applicantId ?? ""),
  });

  const workHistoryData = useMemo(() => {
    return data?.data ?? [];
  }, [data?.data]);

  const [formVisible, setFormVisible] = useState(workHistoryData?.length === 0);

  useEffect(() => {
    if (workHistoryData?.length === 0) {
      setFormVisible(true);
    }
  }, [workHistoryData]);

  const createUpdateWorkHistoryMutation = useMutation({
    mutationFn: createUpdateWorkHistory,
  });

  const deleteWorkHistoryMutation = useMutation({
    mutationFn: deleteWorkHistory,
  });

  const formatDate = (date: string) => {
    const convertToDateObject = new Date(date);
    const formattedDate = format(convertToDateObject, "yyyy-MM-dd");

    return formattedDate;
  };

  const workHistory = workHistoryData?.map((item) => {
    return {
      company: item?.companyName,
      position: item?.position,
      currentJob: item?.isCurrentJob,
      startDate: formatDate(item?.startDate),
      endDate: item?.isCurrentJob ? "" : formatDate(item?.endDate),
      upload: null,
    };
  });

  const handleCreateUpdateWorkHistory = async (values: WorkHistory) => {
    const formData = new FormData();

    formData.append("Id", activeWorkHistoryId.toString());
    formData.append("ApplicantId", user?.applicantId ?? "");
    formData.append("CompanyName", values.company);
    formData.append("Position", values.position);
    formData.append("IsCurrentJob", String(values.currentJob));
    formData.append("StartDate", values.startDate);
    formData.append("EndDate", values.currentJob ? "" : values.endDate);
    if (values.upload) {
      formData.append("Emplyment", values.upload);
    }

    try {
      await createUpdateWorkHistoryMutation.mutateAsync(formData, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });

          setFormVisible(false);
          queryClient.refetchQueries({ queryKey: ["get-work-history"] });
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const handleDeleteWorkHistory = async (id: number) => {
    try {
      await deleteWorkHistoryMutation.mutateAsync(id, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });

          queryClient.refetchQueries({ queryKey: ["get-work-history"] });
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const handleEdit = (index: number, id: number) => {
    setEditIndex((prevIndex) => (prevIndex === index ? null : index));
    setActiveWorkHistoryId(id);
    setFormVisible(false);
  };

  const handleAddMore = () => {
    setFormVisible(true);
    setEditIndex(null);
    setActiveWorkHistoryId(0);
  };

  const handlePrevState = () => {
    setPageIndex(pageIndex > 1 ? pageIndex - 1 : pageIndex);
  };

  const handleNextClick = () => {
    if (workHistoryData?.length === 0) {
      notification.error({
        message: "Error",
        description: "Please completely fill the form to proceed",
      });
    } else {
      setPageIndex(pageIndex + 1);
    }
  };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (isError) {
    return <div>{error?.message}</div>;
  }

  return (
    <>
      <section className={styles.section}>
        <h5>Work History</h5>
        <p>All fields marked with a (*) are required.</p>
      </section>

      <hr />
      <div className={styles.docSection}>
        {workHistoryData?.map((item, index) => (
          <div key={item?.id}>
            <section className="space-between">
              <div>
                <h5>{item?.companyName}</h5>
                <p className={styles.date}>
                  {formatMonthYear(item?.startDate)} -{" "}
                  {item?.isCurrentJob
                    ? "till date"
                    : formatMonthYear(item?.endDate)}
                </p>
              </div>

              <div className="small-gap">
                <button
                  type="button"
                  className={styles.transparentBtn}
                  onClick={() => handleEdit(index, item?.id)}
                >
                  <EditIcon />
                </button>

                <button
                  type="button"
                  className={styles.transparentBtn}
                  onClick={() => handleDeleteWorkHistory(item?.id)}
                >
                  <TrashIcon />
                </button>
              </div>
            </section>

            {editIndex === index && (
              <WorkHistoryForm
                editIndex={editIndex}
                workHistory={workHistory}
                buttonText="Update"
                certificateUrl={item?.employmentUrl}
                isLoading={createUpdateWorkHistoryMutation?.isPending}
                onSubmit={handleCreateUpdateWorkHistory}
              />
            )}
          </div>
        ))}

        {workHistoryData?.length > 1 && <hr />}

        {formVisible && (
          <WorkHistoryForm
            editIndex={editIndex}
            workHistory={workHistory}
            buttonText="Create"
            isLoading={createUpdateWorkHistoryMutation?.isPending}
            onSubmit={handleCreateUpdateWorkHistory}
          />
        )}

        {!formVisible && (
          <button onClick={handleAddMore} className={styles.addQualification}>
            <div>
              <span>+</span>
            </div>

            <span>
              <b>Add Work</b>
            </span>
          </button>
        )}

        <OnboardingButtonGroup
          onClick={handlePrevState}
          onNextClick={handleNextClick}
        />
      </div>
    </>
  );
};

export default WorkHistory;
